import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const BrokerSettings = () => {
  const [formData, setFormData] = useState({
    metaVersion: "5",
    brokerName: "",
    server: "",
    metaAccount: "",
    masterPassword: ""
  });
  const [broker, setBroker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const UserId = localStorage.getItem("UserId");

  // Fetch broker
  const fetchBroker = async () => {
    if (!UserId) return;
    
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/broker-settings`, { userId: UserId });
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const brokerData = response.data.data[0];
        setBroker(brokerData);
        setFormData({
          metaVersion: brokerData.metaVersion || "5",
          brokerName: brokerData.brokerName || "",
          server: brokerData.server || "",
          metaAccount: brokerData.metaAccount || "",
          masterPassword: ""
        });
      } else {
        setBroker(null);
        resetForm();
      }
    } catch (error) {
      console.error("Error fetching broker:", error);
      toast.error("Failed to load broker settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBroker();
  }, [UserId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Add/Update broker
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brokerName || !formData.metaAccount || !formData.masterPassword) {
      toast.error("Please fill all required fields");
      return;
    }

    setSubmitting(true);
    
    try {
      const url = broker ? `${API_BASE}/update-broker` : `${API_BASE}/add-broker`;
      const payload = {
        userId: UserId,
        metaVersion: formData.metaVersion || "5",
        brokerName: formData.brokerName,
        server: formData.server || "",
        metaAccount: formData.metaAccount,
        masterPassword: formData.masterPassword,
        id: broker?.id || undefined
      };
      
      const response = await axios.post(url, payload);
      
      if (response.data.success) {
        toast.success(broker ? "Broker updated successfully!" : "Broker added successfully!");
        fetchBroker();
        setIsEditing(false);
      } else {
        toast.error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving broker:", error);
      toast.error(error.response?.data?.message || "Failed to save broker settings");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      metaVersion: "5",
      brokerName: "",
      server: "",
      metaAccount: "",
      masterPassword: ""
    });
    setShowPassword(false);
  };

  const enableEditing = () => {
    if (broker) {
      setFormData({
        metaVersion: broker.metaVersion || "5",
        brokerName: broker.brokerName || "",
        server: broker.server || "",
        metaAccount: broker.metaAccount || "",
        masterPassword: ""
      });
    }
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (broker) {
      setFormData({
        metaVersion: broker.metaVersion || "5",
        brokerName: broker.brokerName || "",
        server: broker.server || "",
        metaAccount: broker.metaAccount || "",
        masterPassword: ""
      });
    } else {
      resetForm();
    }
    setIsEditing(false);
    setShowPassword(false);
  };

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="broker-settings-page">
        <div className="page-header">
          <div>
            <h1>Broker Settings</h1>
            <p>Manage your MT5 broker connection</p>
          </div>
        </div>

        {/* Form Section */}
        <div className="broker-form-card">
          <h2>{broker ? "Broker Details" : "Add Broker"}</h2>
          
          {loading ? (
            <div className="loading-state">
              <i className="fa fa-spinner fa-spin"></i> Loading broker details...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="broker-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Meta Version *</label>
                  <select
                    name="metaVersion"
                    value={formData.metaVersion}
                    onChange={handleChange}
                    disabled={!isEditing && broker}
                    required
                  >
                    <option value="5">MetaTrader 5 (MT5)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Broker Name *</label>
                  <input
                    type="text"
                    name="brokerName"
                    value={formData.brokerName}
                    onChange={handleChange}
                    placeholder="Enter broker name"
                    disabled={!isEditing && broker}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Server</label>
                  <input
                    type="text"
                    name="server"
                    value={formData.server}
                    onChange={handleChange}
                    placeholder="Enter server name (e.g., Seaprimecapitals-Live)"
                    disabled={!isEditing && broker}
                  />
                </div>
                <div className="form-group">
                  <label>Meta Account *</label>
                  <input
                    type="text"
                    name="metaAccount"
                    value={formData.metaAccount}
                    onChange={handleChange}
                    placeholder="Enter MT5 account number"
                    disabled={!isEditing && broker}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Master Password *</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="masterPassword"
                      value={formData.masterPassword}
                      onChange={handleChange}
                      placeholder="Enter trading account password"
                      disabled={!isEditing && broker}
                      required={!broker || isEditing}
                    />
                    {(isEditing || !broker) && (
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <div className="form-actions">
                    {!broker ? (
                      <button type="submit" className="btn-primary" disabled={submitting}>
                        {submitting ? (
                          <>
                            <i className="fa fa-spinner fa-spin"></i> Adding...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-save"></i> Add Broker
                          </>
                        )}
                      </button>
                    ) : (
                      <>
                        {!isEditing ? (
                          <button type="button" className="btn-primary" onClick={enableEditing}>
                            <i className="fa fa-edit"></i> Edit Broker
                          </button>
                        ) : (
                          <>
                            <button type="submit" className="btn-primary" disabled={submitting}>
                              {submitting ? (
                                <>
                                  <i className="fa fa-spinner fa-spin"></i> Updating...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-save"></i> Update Broker
                                </>
                              )}
                            </button>
                            <button type="button" className="btn-secondary" onClick={cancelEditing}>
                              <i className="fa fa-times"></i> Cancel
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Display Broker Details when not editing */}
        {broker && !isEditing && (
          <div className="broker-details-card">
            <h2>Current Broker Connection</h2>
            <div className="details-grid">
              <div className="detail-item">
                <label>Meta Version</label>
                <span className="version-badge">MT{broker.metaVersion || '5'}</span>
              </div>
              <div className="detail-item">
                <label>Broker Name</label>
                <span>{broker.brokerName}</span>
              </div>
              <div className="detail-item">
                <label>Server</label>
                <span>{broker.server || "-"}</span>
              </div>
              <div className="detail-item">
                <label>Meta Account</label>
                <span className="account-number">{broker.metaAccount}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BrokerSettings;